import {
  formatJstDateTime,
  instantFromEpochMilliseconds,
} from "@nijiurachan/js/util/time";
import { useMemo } from "react";
import { FiX } from "react-icons/fi";
import { useNgStore } from "@/features/ng-filter/stores";
import {
  createNgSettingsMediator,
  isNgSettingsEvent,
  type NgDraftField,
  type NgSettingsEffect,
  type NgSettingsState,
} from "@/features/settings/mediators/ngSettingsMediator";
import {
  isSettingsEvent,
  type SettingsEvent,
} from "@/features/settings/mediators/settingsEvents";
import { SettingRow, SettingSection } from "@/features/settings/ui";
import { ThreadNgWordEditor } from "@/features/thread/components/forms/ThreadNgWordEditor";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
import { Button, HashBitmap, Input, Toggle } from "@/shared/ui/form";
import { TextLink } from "@/shared/ui/navigation";

type InputChangeEvent = React.ChangeEvent<HTMLInputElement, HTMLInputElement>;
type NgStoreState = ReturnType<typeof useNgStore.getState>;

interface NgSettingsViewProps {
  readonly model: Pick<
    NgStoreState,
    | "enabled"
    | "showNgContent"
    | "hiddenThreadIds"
    | "ngDisplayIds"
    | "ngTitles"
    | "ngWords"
    | "ngRegexes"
    | "ngImages"
  >;
  readonly drafts: NgSettingsState;
  readonly renderHiddenThreadLink: (
    threadId: string | number,
  ) => React.ReactNode;
  readonly threadNgPresetInput: string;
  readonly onEvent: (event: SettingsEvent) => void;
}

/**
 * NG設定タブのコンテンツ
 */
export const NgSettings: React.FunctionComponent = () => {
  const {
    enabled,
    showNgContent,
    hiddenThreadIds,
    ngDisplayIds,
    ngTitles,
    ngWords,
    ngRegexes,
    ngImages,
    setEnabled,
    setShowNgContent,
    unhideThread,
    addNgDisplayId,
    removeNgDisplayId,
    addNgTitle,
    removeNgTitle,
    addNgWord,
    removeNgWord,
    addNgRegex,
    removeNgRegex,
    addNgImage,
    removeNgImage,
    clearNgImages,
  } = useNgStore();
  const mediator = useMemo(() => {
    let instance: ReturnType<typeof createNgSettingsMediator>;
    const runEffect = (effect: NgSettingsEffect): void => {
      if (effect.type === "alert") {
        alert(effect.message);
        return;
      }
      if (effect.type === "confirm-clear-images") {
        if (confirm("画像NGをすべて削除しますか？")) clearNgImages();
        return;
      }
      if (effect.type === "set-boolean") {
        (effect.field === "enabled" ? setEnabled : setShowNgContent)(
          effect.value,
        );
        return;
      }
      if (effect.type === "add" && effect.field === "image") {
        const result = addNgImage(effect.value, effect.note ?? "");
        instance.dispatch({
          type: "settings/ng-image-add-completed",
          value: effect.value,
          note: effect.note ?? "",
          success: result.success,
          message: result.message,
        });
        return;
      }
      const actions = {
        displayId: { add: addNgDisplayId, remove: removeNgDisplayId },
        title: { add: addNgTitle, remove: removeNgTitle },
        word: { add: addNgWord, remove: removeNgWord },
        regex: { add: addNgRegex, remove: removeNgRegex },
        image: { add: () => {}, remove: removeNgImage },
        hiddenThread: { add: () => {}, remove: unhideThread },
      };
      actions[effect.field][effect.type](effect.value as never);
    };
    instance = createNgSettingsMediator(runEffect);
    return instance;
  }, [
    addNgDisplayId,
    addNgImage,
    addNgRegex,
    addNgTitle,
    addNgWord,
    clearNgImages,
    removeNgDisplayId,
    removeNgImage,
    removeNgRegex,
    removeNgTitle,
    removeNgWord,
    setEnabled,
    setShowNgContent,
    unhideThread,
  ]);
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) => {
    if (!isSettingsEvent(event) || !isNgSettingsEvent(event)) {
      return "unhandled";
    }
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const drafts = useMediatorSnapshot(mediator);

  return (
    <ComponentScope node={node}>
      <NgSettingsView
        model={{
          enabled,
          showNgContent,
          hiddenThreadIds,
          ngDisplayIds,
          ngTitles,
          ngWords,
          ngRegexes,
          ngImages,
        }}
        drafts={drafts}
        threadNgPresetInput={drafts.threadNgPresetInput}
        renderHiddenThreadLink={(
          threadId: string | number,
        ): React.ReactNode => (
          <TextLink
            to="/thread/$threadId"
            params={{ threadId: String(threadId) }}
            variant="primary"
          >
            {threadId}
          </TextLink>
        )}
        onEvent={(event): void => {
          node.dispatch(event);
        }}
      />
    </ComponentScope>
  );
};

export const NgSettingsView: React.FunctionComponent<NgSettingsViewProps> = ({
  model: {
    enabled,
    showNgContent,
    hiddenThreadIds,
    ngDisplayIds,
    ngTitles,
    ngWords,
    ngRegexes,
    ngImages,
  },
  drafts,
  threadNgPresetInput,
  renderHiddenThreadLink,
  onEvent,
}: NgSettingsViewProps) => {
  const changeDraft = (field: NgDraftField, value: string): void => {
    onEvent({ type: "settings/ng-draft-changed", field, value });
  };
  const requestAdd = (
    field: "displayId" | "title" | "word" | "regex" | "image",
  ): void => {
    onEvent({ type: "settings/ng-add-requested", field });
  };

  return (
    <>
      <SettingSection title="スレッドNGワードのプリセット">
        <div className="p-4">
          <p className="mb-3 text-xs text-muted-foreground">
            スレ作成時に使う正規表現。閲覧者のNG設定とは別です。
          </p>
          <ThreadNgWordEditor
            value={threadNgPresetInput}
            onChange={(value: string): void =>
              onEvent({
                type: "settings/ng-thread-preset-input-changed",
                value,
              })
            }
          />
        </div>
      </SettingSection>

      <SettingSection title="NG表示ID" description="投稿者の表示IDで非表示">
        <SettingRow>
          <Input
            type="text"
            placeholder="表示ID（例: ABC123）"
            className="w-full"
            value={drafts.displayId}
            onChange={(e: InputChangeEvent): void =>
              changeDraft("displayId", e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent): void => {
              if (e.key === "Enter") {
                requestAdd("displayId");
              }
            }}
          />
          <Button
            variant="primary"
            onClick={(): void => requestAdd("displayId")}
          >
            追加
          </Button>
        </SettingRow>
        {ngDisplayIds.length > 0 ? (
          <ul aria-label="NG表示ID" className="m-0 list-none space-y-2 p-0">
            {ngDisplayIds.map(({ id: displayId }) => (
              <li key={displayId}>
                <SettingRow>
                  <data
                    value={displayId}
                    className="min-w-0 flex-1 text-foreground text-destructive"
                  >
                    {displayId}
                  </data>
                  <Button
                    variant="ghost"
                    onClick={(): void =>
                      onEvent({
                        type: "settings/ng-remove-requested",
                        field: "displayId",
                        value: displayId,
                      })
                    }
                    icon={<FiX size={16} />}
                  >
                    削除
                  </Button>
                </SettingRow>
              </li>
            ))}
          </ul>
        ) : (
          <SettingRow>
            <p className="text-muted-foreground">なし</p>
          </SettingRow>
        )}
      </SettingSection>

      <SettingSection title="NGスレ文" description="本文部分一致で非表示">
        <SettingRow>
          <Input
            type="text"
            placeholder="NGスレ文を入力"
            className="w-full"
            value={drafts.title}
            onChange={(e: InputChangeEvent): void =>
              changeDraft("title", e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent): void => {
              if (e.key === "Enter") {
                requestAdd("title");
              }
            }}
          />
          <Button variant="primary" onClick={(): void => requestAdd("title")}>
            追加
          </Button>
        </SettingRow>
        {ngTitles.length > 0 ? (
          <ul aria-label="NGスレ文" className="m-0 list-none space-y-2 p-0">
            {ngTitles.map((title) => (
              <li key={title}>
                <SettingRow>
                  <p className="min-w-0 flex-1 truncate text-foreground">
                    {title}
                  </p>
                  <Button
                    variant="ghost"
                    onClick={(): void =>
                      onEvent({
                        type: "settings/ng-remove-requested",
                        field: "title",
                        value: title,
                      })
                    }
                    icon={<FiX size={16} />}
                  >
                    削除
                  </Button>
                </SettingRow>
              </li>
            ))}
          </ul>
        ) : (
          <SettingRow>
            <p className="text-muted-foreground">なし</p>
          </SettingRow>
        )}
      </SettingSection>

      <SettingSection title="NGワード" description="本文部分一致で非表示">
        <SettingRow>
          <Input
            type="text"
            placeholder="NGワードを入力"
            className="w-full"
            value={drafts.word}
            onChange={(e: InputChangeEvent): void =>
              changeDraft("word", e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent): void => {
              if (e.key === "Enter") {
                requestAdd("word");
              }
            }}
          />
          <Button variant="primary" onClick={(): void => requestAdd("word")}>
            追加
          </Button>
        </SettingRow>
        {ngWords.length > 0 ? (
          <ul aria-label="NGワード" className="m-0 list-none space-y-2 p-0">
            {ngWords.map((word) => (
              <li key={word}>
                <SettingRow>
                  <p className="min-w-0 flex-1 truncate text-foreground">
                    {word}
                  </p>
                  <Button
                    variant="ghost"
                    onClick={(): void =>
                      onEvent({
                        type: "settings/ng-remove-requested",
                        field: "word",
                        value: word,
                      })
                    }
                    icon={<FiX size={16} />}
                  >
                    削除
                  </Button>
                </SettingRow>
              </li>
            ))}
          </ul>
        ) : (
          <SettingRow>
            <p className="text-muted-foreground">なし</p>
          </SettingRow>
        )}
      </SettingSection>

      <SettingSection title="NG正規表現" description="正規表現パターンで非表示">
        <SettingRow>
          <Input
            type="text"
            placeholder="正規表現パターン（例: ^spam.*）"
            className="w-full"
            value={drafts.regex}
            onChange={(e: InputChangeEvent): void =>
              changeDraft("regex", e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent): void => {
              if (e.key === "Enter") {
                requestAdd("regex");
              }
            }}
          />
          <Button variant="primary" onClick={(): void => requestAdd("regex")}>
            追加
          </Button>
        </SettingRow>
        {ngRegexes.length > 0 ? (
          <ul aria-label="NG正規表現" className="m-0 list-none space-y-2 p-0">
            {ngRegexes.map((regex) => (
              <li key={regex}>
                <SettingRow>
                  <code className="min-w-0 flex-1 truncate text-sm text-foreground">
                    {regex}
                  </code>
                  <Button
                    variant="ghost"
                    onClick={(): void =>
                      onEvent({
                        type: "settings/ng-remove-requested",
                        field: "regex",
                        value: regex,
                      })
                    }
                    icon={<FiX size={16} />}
                  >
                    削除
                  </Button>
                </SettingRow>
              </li>
            ))}
          </ul>
        ) : (
          <SettingRow>
            <p className="text-muted-foreground">なし</p>
          </SettingRow>
        )}
      </SettingSection>

      <SettingSection title="NG画像" description="ハッシュから類似画像を非表示">
        <SettingRow>
          <Input
            type="text"
            placeholder="画像ハッシュ（64桁二進数 またはそれのURLセーフbase64エンコード）"
            className="w-full"
            value={drafts.image}
            onChange={(e: InputChangeEvent): void =>
              changeDraft("image", e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent): void => {
              if (e.key === "Enter") {
                requestAdd("image");
              }
            }}
          />
          <Button variant="primary" onClick={(): void => requestAdd("image")}>
            追加
          </Button>
        </SettingRow>
        <SettingRow>
          <Input
            type="text"
            placeholder="メモ（任意）"
            className="w-full"
            value={drafts.imageNote}
            onChange={(e: InputChangeEvent): void =>
              changeDraft("imageNote", e.target.value)
            }
          />
          {ngImages.length > 0 && (
            <Button
              variant="ghost"
              onClick={(): void =>
                onEvent({ type: "settings/ng-clear-images-requested" })
              }
            >
              全削除
            </Button>
          )}
        </SettingRow>
        {ngImages.length > 0 ? (
          <ul aria-label="NG画像" className="m-0 list-none space-y-2 p-0">
            {ngImages.map((entry) => (
              <li key={entry.hash}>
                <SettingRow>
                  <figure className="flex min-w-0 flex-1 items-center gap-3">
                    <HashBitmap bits={entry.hash} />
                    <figcaption className="min-w-0 flex-1">
                      <code className="block text-foreground text-sm truncate">
                        {entry.hash}
                      </code>
                      {(entry.note || entry.addedAt > 0) && (
                        <p className="text-xs text-muted-foreground truncate">
                          {entry.note || "メモなし"}
                          {entry.addedAt > 0 &&
                            ` · ${formatJstDateTime(
                              instantFromEpochMilliseconds(entry.addedAt),
                              {
                                year: "numeric",
                                month: "numeric",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                              },
                            )}`}
                        </p>
                      )}
                    </figcaption>
                  </figure>
                  <Button
                    variant="ghost"
                    onClick={(): void =>
                      onEvent({
                        type: "settings/ng-remove-requested",
                        field: "image",
                        value: entry.hash,
                      })
                    }
                    icon={<FiX size={16} />}
                  >
                    削除
                  </Button>
                </SettingRow>
              </li>
            ))}
          </ul>
        ) : (
          <SettingRow>
            <p className="text-muted-foreground">なし</p>
          </SettingRow>
        )}
      </SettingSection>

      <SettingSection title="非表示スレ" description="非表示にしたスレの一覧">
        {hiddenThreadIds.length > 0 ? (
          <ul aria-label="非表示スレ" className="m-0 list-none space-y-2 p-0">
            {hiddenThreadIds.map((threadId) => (
              <li key={threadId}>
                <SettingRow>
                  {renderHiddenThreadLink(threadId)}
                  <Button
                    variant="ghost"
                    onClick={(): void =>
                      onEvent({
                        type: "settings/ng-remove-requested",
                        field: "hiddenThread",
                        value: threadId,
                      })
                    }
                    icon={<FiX size={16} />}
                  >
                    削除
                  </Button>
                </SettingRow>
              </li>
            ))}
          </ul>
        ) : (
          <SettingRow>
            <p className="text-muted-foreground">なし</p>
          </SettingRow>
        )}
      </SettingSection>

      <SettingSection title="NGフィルタ">
        <SettingRow label="NGフィルタを有効にする">
          <Toggle
            checked={enabled}
            onChange={(value: boolean): void =>
              onEvent({
                type: "settings/ng-boolean-changed",
                field: "enabled",
                value,
              })
            }
            aria-label="NGフィルタ切替"
          />
        </SettingRow>
        <SettingRow
          label="NGされたコンテンツを表示"
          description="ONにすると、NGされたスレッドやレスがラベル付きで表示されます"
        >
          <Toggle
            checked={showNgContent}
            onChange={(value: boolean): void =>
              onEvent({
                type: "settings/ng-boolean-changed",
                field: "showNgContent",
                value,
              })
            }
            aria-label="NGコンテンツ表示切替"
          />
        </SettingRow>
      </SettingSection>
    </>
  );
};
